// University of California, Davis: public college page (#2596), official
// facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - UC Davis's Common Data Set 2025-2026 (CDS_UCD_0.pdf on AggieData; the
//    file is headed "Common Data Set 2025-2026" and was made in July 2026;
//    the AggieData index page blocks scripted reads, so the file was read
//    directly). C1 gives Fall 2025 applicants and admits, and a residency
//    table. Its enrollees by sex add to 6,810, while its enrollees by status
//    and its residency total both give 6,824; the stated total, 6,824, is
//    used. C8A answers no to using SAT or ACT scores. C21 and C22 answer no
//    to early decision and early action.
//  - UC Davis admissions pages (first-year requirements, first-year
//    timeline) and the UC systemwide first-year dates page.
//  - UC Davis cost page, estimated cost of attendance for students who first
//    enroll in 2026-27 (in-state and nonresident totals as published).
//  - College Scorecard, school id 110644, read from the Scorecard school
//    page (the API's demo key was over its rate limit; same data).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UC Davis Common Data Set 2025-2026',
        url: 'https://aggiedata.ucdavis.edu/sites/g/files/dgvnsk1841/files/media/documents/CDS_UCD_0.pdf',
        read: '2026-09-30',
    },
    timeline: {
        label: 'UC Davis Admissions, Timeline for First-Year Applicants',
        url: 'https://www.ucdavis.edu/admissions/undergraduate/first-year/timeline',
        read: '2026-09-30',
    },
    ucDates: {
        label: 'UC Admissions, First-Year Dates and Deadlines',
        url: 'https://admission.universityofcalifornia.edu/how-to-apply/applying-as-a-first-year/dates-and-deadlines.html',
        read: '2026-09-30',
    },
    tests: {
        label: 'UC Davis Admissions, First-Year Admission Requirements',
        url: 'https://www.ucdavis.edu/admissions/undergraduate/first-year/requirements',
        read: '2026-09-30',
    },
    coa: {
        label: 'UC Davis, Cost of Attendance 2026-27',
        url: 'https://www.ucdavis.edu/admissions/cost',
        read: '2026-09-30',
    },
    ucGuide: {
        label: 'UC Admissions, Quick Reference Guide to UC Admissions (August 2026)',
        url: 'https://admission.universityofcalifornia.edu/counselors/_files/documents/quick-reference-guide-to-uc-admissions.pdf',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110644-University-of-California-Davis',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-california-davis',
    kbId: 'university_of_california_davis',
    name: 'University of California, Davis',
    shortName: 'UC Davis',
    location: 'Davis, CA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (UC Davis brand guide); decoration only.
    branding: {
        primary: '#022851',
        secondary: '#FFBF00',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_california_davis_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 102988,
        admitted: 45674,
        enrolled: 6824,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 68593, admitted: 25473 },
            { group: 'Out-of-state', applied: 13149, admitted: 8315 },
            { group: 'International', applied: 21246, admitted: 11886 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UC Davis\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'UC Davis offers neither. The Common Data Set answers no to both.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'UC application (one filing period)', deadline: 'November 30', decision: 'As early as mid-March', binding: false },
        ],
        note: 'You apply on the UC application, which opens August 1. If you are admitted, your Statement of Intent to Register is due May 1.',
        source: 'timeline',
        binding: 'Nothing is binding. For fall 2027 the University of California has one filing period, October 1 to November 30, with admission decisions in March.',
        bindingSource: 'ucDates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UC Davis is test-blind: SAT and ACT scores are not considered for admission decisions or scholarships, even if you report them. Scores may be used only to meet minimum requirements or for course placement after you enroll.',
        source: 'tests',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores in admission decisions.',
        cdsWhere: 'section C8',
    },

    // Changes already in effect for this cycle (#2651 review): they apply to
    // courses taken from fall 2026 on, so to this year's seniors too.
    currentChanges: [
        { topic: 'Math', line: 'A statistics or additional math course taken in fall 2026 or later no longer makes up for a missing or low grade in Algebra II or Math III. It still counts as an extra year of math. Seniors applying this fall: this already applies to courses you take this year.', source: 'ucGuide' },
        { topic: 'Math', line: 'From fall 2026, AP Statistics no longer meets two years of the UC math requirement. AP Precalculus (with a score of 4 or 5), AP Calculus AB, AP Calculus BC and IB HL Mathematics Analysis and Approaches still do.', source: 'ucGuide' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'timeline',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Systemwide tuition and fees', amounts: [15588, 15588] },
            { item: 'Campus-based fees', amounts: [2490, 2490] },
            { item: 'Nonresident supplemental tuition', amounts: [0, 39270] },
            { item: 'Room and board (residence hall)', amounts: [22252, 22252] },
            { item: 'Books and supplies', amounts: [1205, 1205] },
            { item: 'Personal expenses', amounts: [2656, 2656] },
            { item: 'Transportation', amounts: [672, 672] },
            { item: 'Health insurance', amounts: [3996, 3996] },
            { item: 'Document fee', amounts: [150, 150] },
        ],
        totals: [49009, 88279],
        note: 'The budget for a student who first enrolls in 2026-27 and lives in a residence hall.',
        source: 'coa',
        netPrice: {
            average: 14318,
            byIncome: [
                { income: '$0 to $30,000', amount: 8770 },
                { income: '$30,001 to $48,000', amount: 9042 },
                { income: '$48,001 to $75,000', amount: 11562 },
                { income: '$75,001 to $110,000', amount: 15645 },
                { income: 'Over $110,000', amount: 32902 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UC Davis\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
